import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import "./LoginPage.css";
import { sanitizeReturnPath } from "./authRouting";
import { Form } from "react-bootstrap";
import { DNA } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import { POLICY_URLS, PRODUCT_NAME } from "../brand";

type SignupMode = "approval" | "open" | "closed";

const SignUpPage: React.FC<{ embedded?: boolean; returnPath?: string }> = ({ returnPath }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState("");
  const [params] = useSearchParams();
  const returnUrl = sanitizeReturnPath(returnPath || params.get("returnUrl"), "/");
  const signInUrl = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  // The server decides whether accounts need approval, email verification, or are closed.
  const [mode, setMode] = useState<SignupMode | null>(null);
  const [configError,setConfigError]=useState(false),[configAttempt,setConfigAttempt]=useState(0);
  const [notice, setNotice] = useState<{version:string;hash:string} | null>(null);
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:3201";

  useEffect(() => {
    let active = true; setConfigError(false); setMode(null); setAgreeToTerms(false);
    axios
      .get(`${backendUrl}/api/config`)
      .then(res => {
        if (active) { setMode(res.data?.auth?.signup_mode || "approval"); setNotice(res.data?.policies || null); }
      })
      .catch(() => {
        if (active) setConfigError(true);
      });
    return () => {
      active = false;
    };
  }, [backendUrl,configAttempt]);

  if (mode === "closed") {
    return (
      <div className="signup-container">
        <div className="signup-card">
          <h1>Private pilot access</h1>
          <p className="text-secondary mb-4">
            Reviewed is not accepting new accounts right now.
          </p>
          <div className="login-links">
            <Link to={signInUrl}>Sign in with an existing account</Link>
            <Link to="/work-with-us">Attorneys: apply to review questions</Link>
          </div>
        </div>
      </div>
    );
  }

  // Until the server answers, show the approval form (the default mode).
  const approval = mode === null || mode === "approval";

  const handleSignUp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if(mode===null || configError) return;
    const newErrors: string[] = [];
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.push("Enter a valid email address.");
    }
    if (
      password.length < 8 ||
      !/[a-zA-Z]/.test(password) ||
      !/[0-9]/.test(password)
    ) {
      newErrors.push(t("signup.passwordRequirements"));
    }
    if (password !== passwordConfirmation) {
      newErrors.push("Passwords do not match.");
    }
    if (!agreeToTerms) {
      newErrors.push("You must accept the terms and conditions.");
    }
    setErrors(newErrors);
    if (newErrors.length) return;
    setIsLoading(true);
    try {
      const res = await axios.post(`${backendUrl}/api/signup`, {
        email: email.trim(),
        password,
        ...(notice ? { betaAcceptance: { ...notice, accepted: true } } : {}),
      });
      setDone(res.data?.message || "Thanks. Your request has been received.");
    } catch (error: any) {
      if(error.response?.status===409 && /notice|terms/i.test(error.response?.data?.message||"")) setConfigAttempt(n=>n+1);
      setErrors([
        error.response?.data?.message ||
          "Could not create your account. Try again.",
      ]);
    }
    setIsLoading(false);
  };

  if (done)
    return (
      <div className="signup-container">
        <div className="signup-card">
          <h1>{approval ? "Request received" : "Check your email"}</h1>
          {approval ? (
            <p className="text-secondary">
              Reviewed is in approval-only beta testing. We review each request, which
              usually takes a few days, and email{" "}
              <strong>{email.trim()}</strong> when your account is ready.
            </p>
          ) : (
            <div className="alert alert-success">{done}</div>
          )}
          <div className="login-links">
            <Link to={signInUrl}>Back to sign in</Link>
          </div>
        </div>
      </div>
    );

  return (
    <div className="signup-container">
      {isLoading && loadingAnimation(t)}
      {!isLoading && (
        <div className="signup-card">
          <h1>
            {approval
              ? "Request access to Reviewed"
              : "Create your Reviewed account"}
          </h1>
          <p className="text-secondary mb-4">
            {approval
              ? "Request beta access. We’ll email you once your account is approved. Use generic or made-up information while testing."
              : "Explore a legal question with AI, then request an attorney’s written answer. Agree on scope, price, and turnaround before attorney work begins."}
          </p>
          {errors.map((error, index) => (
            <div key={index} className="alert alert-danger">
              {error}
            </div>
          ))}
          {configError && <div role="alert" className="alert alert-warning">Could not load the current signup notice. <button type="button" className="btn btn-link" onClick={()=>setConfigAttempt(n=>n+1)}>Retry</button></div>}
          <form onSubmit={handleSignUp} noValidate>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                {t("signup.email")}
              </label>
              <input
                type="email"
                className="form-control"
                id="email"
                autoComplete="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                {t("signup.password")}
              </label>
              <input
                type="password"
                className="form-control"
                id="password"
                autoComplete="new-password"
                value={password}
                onChange={event => setPassword(event.target.value)}
              />
              <div className="form-text">
                {t("signup.passwordRequirements")}
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="confirm-password" className="form-label">
                {t("signup.confirmPassword")}
              </label>
              <input
                type="password"
                className="form-control"
                id="confirm-password"
                autoComplete="new-password"
                value={passwordConfirmation}
                onChange={event => setPasswordConfirmation(event.target.value)}
              />
            </div>
            <Form.Group className="mb-4" controlId="formBasicCheckbox">
              <Form.Check
                type="checkbox"
                label={
                  <span>
                    {t("signup.agreeToTerms", { product: PRODUCT_NAME })}{" "}
                    <a
                      href={POLICY_URLS.privacy}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {t("signup.privacyPolicy")}
                    </a>
                    {", "}
                    <a
                      href={POLICY_URLS.betaAgreement}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {t("signup.betaAgreement")}
                    </a>
                    {", " + t("signup.and") + " "}
                    <a
                      href={POLICY_URLS.terms}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {t("signup.termsOfService")}
                    </a>{", "}<a href={POLICY_URLS.privilege} target="_blank" rel="noreferrer noopener">Confidentiality notice</a>
                  </span>
                }
                checked={agreeToTerms}
                onChange={event => setAgreeToTerms(event.target.checked)}
              />
            </Form.Group>
            <button type="submit" disabled={mode===null || configError} className="submit-button-signup mb-4">
              {approval ? "Request access" : t("signup.signUpButton")}
            </button>
          </form>
          <div className="login-links">
            <Link to={signInUrl}>
              {t("signup.alreadyHaveAccount")} {t("signup.logIn")}
            </Link>
            <Link to="/work-with-us">Attorneys: apply to review questions</Link>
          </div>
        </div>
      )}
    </div>
  );
};

const loadingAnimation = (t: any) => {
  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ flexDirection: "column" }}
    >
      <div className="">
        <DNA
          visible={true}
          height="80"
          width="80"
          ariaLabel="dna-loading"
          wrapperStyle={{}}
          wrapperClass="dna-wrapper"
        />
      </div>
      <b>{t("signup.loadingMessage")}</b>
    </div>
  );
};

export default SignUpPage;
